// copied, with modifications, from correlate-app/widgets/datepicker.js

import moment from 'moment';
import {Component} from 'panel';

import {normalizeDateRange, normalizeDateStrings, parseDate} from '../../../../../util/date';
import {registerMPElement} from '../../../../../util/register-element';
import {pluralize} from '../../../../../util/index';

import template from './index.jade';
import css from './index.styl?inline';

import './date-input';

const FOCUSED_INPUT = {
  NONE: null,
  FROM: `from`,
  TO: `to`,
};

export default registerMPElement(
  `cohorts-calendar-input`,
  class DoubleCalendar extends Component {
    static get observedAttributes() {
      return Component.observedAttributes.concat([`from`, `is-double-calendar`, `is-range`, `min-days-apart`, `to`]);
    }
    attributeChangedCallback(attr, oldValue, newValue) {
      super.attributeChangedCallback(...arguments);
      switch (attr) {
        case `from`:
          this.update({from: newValue});
          break;
        case `is-range`:
          this.update({isRangePicker: this.isAttributeEnabled(attr)});
          break;
        case `to`:
          this.update({to: newValue});
          break;
        default:
          break;
      }
    }

    get config() {
      return {
        useShadowDom: true,
        css,
        template,
        defaultState: {
          errorMessage: ``,
          focused: FOCUSED_INPUT.NONE,
          from: ``,
          isRangePicker: true,
          to: ``,
        },
        helpers: {
          getCalendarAttrs: () => {
            const attrs = {
              'is-double-calendar': this.isAttributeEnabled(`is-double-calendar`),
            };
            if (this.state.isRangePicker) {
              Object.assign(attrs, {
                'is-from-date-focused': this.state.focused === FOCUSED_INPUT.FROM,
                'is-range': true,
                'is-to-date-focused': this.state.focused === FOCUSED_INPUT.TO,
                'selected-from-date': this.state.from,
                'selected-to-date': this.state.to,
              });
            } else {
              Object.assign(attrs, {
                'selected-date': this.state.from,
              });
            }
            return attrs;
          },
          selectedDate: (ev) => {
            if (this.state.isRangePicker) {
              let {from = this.state.from, to = this.state.to} = ev.detail;
              [from, to] = normalizeDateStrings([from, to]);
              this.selectDateRange(from, to);
            } else {
              this.selectSingleDate(ev.detail.date);
            }
          },
          changedFrom: (ev) => {
            if (this.state.isRangePicker) {
              const [from, to] = normalizeDateRange(ev.detail, this.state.to, `from`);
              this.selectDateRange(from, to);
            } else {
              this.selectSingleDate(ev.detail);
            }
          },
          changedTo: (ev) => {
            const [from, to] = normalizeDateRange(this.state.from, ev.detail, `to`);
            this.selectDateRange(from, to);
          },
          blurredFrom: () => {
            if (this.state.focused === FOCUSED_INPUT.FROM) {
              this.update({focused: FOCUSED_INPUT.NONE});
            }
          },
          focusedFrom: () => {
            this.update({focused: FOCUSED_INPUT.FROM});
          },
          blurredTo: () => {
            if (this.state.focused === FOCUSED_INPUT.TO) {
              this.update({focused: FOCUSED_INPUT.NONE});
            }
          },
          focusedTo: () => {
            this.update({focused: FOCUSED_INPUT.TO});
          },
          onDraw: () => this.dispatchEvent(new CustomEvent(`draw`, {bubbles: true, composed: true})),
        },
      };
    }

    daysApart(from, to) {
      return moment(to).diff(moment(from), `days`);
    }

    emitChange() {
      const {from, to} = this.state;
      this.dispatchEvent(new CustomEvent(`change`, {detail: {from, to}}));
    }

    selectDateRange(from, to) {
      if (!parseDate(from) || (to && !parseDate(to))) {
        return;
      }

      this.update({
        errorMessage: ``,
        from,
        to: to || ``,
      });
      if (from && to) {
        const daysApart = this.daysApart(from, to);
        const minDaysApart = this.getNumberAttribute(`min-days-apart`) || 0;
        if (daysApart >= minDaysApart) {
          this.emitChange();
        } else {
          this.update({
            errorMessage: `Dates must be at least ${minDaysApart} ${pluralize(`day`, minDaysApart, `days`)} apart.`,
          });
        }
      }
    }

    focusFromInput() {
      this.fromInput.focus();
    }

    selectFromInput() {
      this.fromInput.select();
    }

    focusToInput() {
      this.toInput.focus();
    }

    selectToInput() {
      this.toInput.select();
    }

    selectSingleDate(date) {
      const [from] = normalizeDateStrings([date]);
      if (parseDate(from)) {
        this.update({from});
        this.emitChange();
      }
    }

    get fromInput() {
      return this.el.querySelector(`cohorts-date-input.from-date-input`);
    }

    get toInput() {
      return this.el.querySelector(`cohorts-date-input.to-date-input`);
    }
  },
);
